import cls from "@fvilers/cls";
import { PlusIcon } from "@imi/icons/PlusIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import { Popover } from "@imi/ui/components/Popover/Popover.js";
import { PopoverContent } from "@imi/ui/components/Popover/PopoverContent.js";
import { PopoverTrigger } from "@imi/ui/components/Popover/PopoverTrigger.js";
import { ScrollShadow } from "@imi/ui/components/ScrollShadow/ScrollShadow.js";
import { Align, Side } from "@imi/ui/hooks/useRelativePosition/types.js";
import { Column, RowData, TableFeatures } from "@tanstack/react-table";
import { group, isString } from "radashi";
import { ComponentProps, useEffect, useRef, useState } from "react";
import styles from "./ColumnsVisibilityBadges.module.css";

export type GroupedColumn<T extends RowData> = {
  column: Column<TableFeatures, T>;
  group?: string | null;
};

type ColumnsVisibilityBadgesProps<T extends RowData> = {
  align?: Align | undefined;
  className?: string;
  columns: GroupedColumn<T>[];
  extraColumnsLabel: (count: number) => string;
  nbDisplayedColumns?: number;
  side?: Side | undefined;
} & ComponentProps<"div">;

export function ColumnsVisibilityBadges<T extends RowData>({
  align = "center",
  className,
  columns,
  extraColumnsLabel,
  nbDisplayedColumns = 3,
  side = "bottom",
  ...props
}: ColumnsVisibilityBadgesProps<T>) {
  const [minWidth, setMinWidth] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);

  const firstColumns = columns.slice(0, nbDisplayedColumns);
  const extraColumns = columns.slice(nbDisplayedColumns);
  const groupedExtraColumns = group(extraColumns, ({ group }) => group ?? "");

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return;
    const updateContentMinWidth = () => {
      if (popover) {
        setMinWidth(popover.offsetWidth);
      }
    };

    updateContentMinWidth();
    const observer = new ResizeObserver(updateContentMinWidth);
    observer.observe(popover);
    return () => {
      observer.disconnect();
    };
  }, [popoverRef, extraColumns?.length]);

  if (!columns.length) return null;

  return (
    <div
      className={cls(styles.ColumnsVisibilityBadges, className)}
      data-testid="columns-visibility-badges"
      {...props}
    >
      {firstColumns.map(({ column }) => (
        <Button
          className={styles.ShowColumnButton}
          key={column.id}
          onClick={() => column.toggleVisibility(true)}
          size="sm"
          variant="outline"
        >
          <PlusIcon />
          {getColumnHeader(column)}
        </Button>
      ))}

      {extraColumns.length > 0 && (
        <div ref={popoverRef}>
          <Popover>
            <PopoverTrigger
              className={styles.ShowColumnButton}
              size="sm"
              variant="outline"
            >
              <PlusIcon />
              <span>{extraColumnsLabel(extraColumns.length)}</span>
            </PopoverTrigger>
            <PopoverContent
              align={align}
              className={styles.ColumnsVisibilityPopoverContent}
              data-testid="columns-visibility-popover-content"
              keyboardNavigation
              side={side}
              style={
                {
                  "--content-min-width": `${minWidth + 20}px`,
                } as React.CSSProperties
              }
            >
              <ScrollShadow
                as="ul"
                className={styles.ScrollShadow}
                shadowSize={30}
              >
                {Object.entries(groupedExtraColumns).map(([group, columns]) => (
                  <li className={styles.ExtraColumnGroup} key={group}>
                    {group !== "" && <span>{group}</span>}
                    <ul className={styles.ExtraColumnList}>
                      {columns?.map(({ column }) => (
                        <li key={column.id}>
                          <Button
                            className={cls(
                              styles.ShowColumnButton,
                              styles.ExtraColumnButton,
                            )}
                            onClick={() => column.toggleVisibility(true)}
                            size="sm"
                            variant="outline"
                          >
                            <PlusIcon />
                            {getColumnHeader(column)}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ScrollShadow>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}

function getColumnHeader<T extends RowData>(column: Column<TableFeatures, T>) {
  return column.columnDef.header && isString(column.columnDef.header)
    ? column.columnDef.header
    : column.id;
}
